import { z } from 'zod';

export type ValidationTranslate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Zod's built-in messages are English, and nothing localised them: 21 of the portal's 22
 * schemas take no `t`, so a Vietnamese user submitting an empty required field read
 * "String must contain at least 1 character(s)".
 *
 * `zod-i18n-map` is the usual answer and was already a dependency here, but it ships no
 * `vi` locale (30 languages, Vietnamese is not among them), so adopting it would have left
 * the PRIMARY locale in English — the bug it was supposed to fix. It has been dropped from
 * package.json rather than left installed and unused. This maps the handful of
 * issue codes the portal's schemas can actually raise onto the project's own locale files
 * instead. The validators in use across those schemas are min, max, email, regex,
 * nonnegative and int; everything else falls through to zod's default on purpose, so an
 * unexpected code shows something rather than an empty string.
 *
 * Pure and parameterised by `t` rather than reaching for the i18next singleton, so it can
 * be tested without standing up i18n.
 */
export function makeZodErrorMap(t: ValidationTranslate): z.ZodErrorMap {
  return (issue, ctx) => {
    switch (issue.code) {
      case z.ZodIssueCode.invalid_type:
        // Formik rewrites every untouched '' to undefined before validating, so a missing
        // required field arrives here rather than as too_small — and a blank numeric field
        // arrives as NaN. Both read as "required" to the person filling the form in.
        if (issue.received === 'undefined' || issue.received === 'null' || issue.received === 'nan') {
          return { message: t('validation.required') };
        }
        if (issue.expected === 'number') {
          return { message: t('validation.number') };
        }
        break;
      case z.ZodIssueCode.too_small:
        if (issue.type === 'string') {
          // min(1) is "you left it blank", not "it is too short".
          return issue.minimum === 1
            ? { message: t('validation.required') }
            : { message: t('validation.minLength', { count: Number(issue.minimum) }) };
        }
        return { message: t('validation.min', { min: Number(issue.minimum) }) };
      case z.ZodIssueCode.too_big:
        if (issue.type === 'string') {
          return { message: t('validation.maxLength', { count: Number(issue.maximum) }) };
        }
        return { message: t('validation.max', { max: Number(issue.maximum) }) };
      case z.ZodIssueCode.invalid_string:
        return issue.validation === 'email' ? { message: t('validation.email') } : { message: t('validation.pattern') };
      default:
        break;
    }
    // Includes ZodIssueCode.custom: refine/superRefine already carry their own message.
    return { message: ctx.defaultError };
  };
}
